// Panel > Admin > Müşteriler > Raporlar (yalnızca sistem adminleri): paylaşılabilir canlı raporun (/r/:slug) yönetimi.
// Bölümler, varsayılan görünüm, yetkili e-postalar, otomatik PDF gönderimi, AI yorumu ve erişim kaydı.
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, SectionHeader, T } from '../poyais-ui';
import { getToken } from '../auth';
import ReportIntegrations from './ReportIntegrations';
import PoyaisSpinner from '../PoyaisSpinner';

const API = '/api/v1/reports';

interface Viewer { id: number; email: string; last_login_at: string | null; created_at: string; personal_link: string | null }
interface Report {
  id: number; client_id: number; client_name: string; slug: string; title: string;
  sections: { order: string[]; disabled: string[] };
  default_preset: string; fixed_start?: string | null; fixed_end?: string | null; service_day: number | null; compare_default: boolean; compare_mode?: 'prev' | 'yoy'; is_active: boolean;
  weekly_enabled: boolean; monthly_enabled: boolean;
  next_weekly_period: [string, string]; next_monthly_period: [string, string];
  viewers: Viewer[]; sources: { ga4: boolean; google_ads: boolean; meta_ads: boolean; pixa?: boolean; orders?: boolean };
}
interface LogEntry { email: string; action: string; detail: string; created_at: string }

const SECTIONS: Record<string, { name: string; detail: string; needs: (s: Report['sources']) => boolean }> = {
  summary: { name: 'Özet', detail: 'KPI kartları, günlük grafik ve kanala göre gelir', needs: (s) => s.ga4 || s.google_ads || s.meta_ads },
  commentary: { name: 'Dönem Yorumu', detail: 'Poyais One AI · rapor verisinden otomatik üretilir', needs: (s) => s.ga4 || s.google_ads || s.meta_ads },
  ads: { name: 'Reklam Kanalları', detail: 'Google Ads ve Meta Ads kampanya tabloları', needs: (s) => s.google_ads || s.meta_ads },
  traffic: { name: 'Trafik ve Dönüşüm', detail: 'GA4 · e-ticaret hunisi, kaynak / araç', needs: (s) => s.ga4 },
  products: { name: 'Ürünler (Shop)', detail: 'GA4 · web sitesi ürün metrikleri, model kodu', needs: (s) => s.ga4 },
  cart_drop: { name: 'Sepete Eklenen Ama Satışa Dönmeyen Ürünler', detail: 'GA4 · sepete eklenip hiç satılmayan ürünler, AI özeti', needs: (s) => s.ga4 },
  view_drop: { name: 'Görüntülenen Ama Sepete Atılmayan Ürünler', detail: 'GA4 · ilgi görüp sepete eklenmeyen ürünler, AI özeti', needs: (s) => s.ga4 },
  marketplace: { name: 'Ürünler (Tümü)', detail: 'Sipariş verisi · web sitesi ve pazaryerleri dahil tüm kanallarda ürün satışları', needs: (s) => !!s.orders },
  marketplaces: { name: "Marketplace'ler", detail: 'Sipariş verisi · kanal bazında sipariş, adet, gelir, en çok satanlar ve stokları, AI özeti', needs: (s) => !!s.orders },
  revenue_returns: { name: 'Ciro ve İadeler', detail: 'Strateji · brüt / net ciro, adet, platform bazında iadeler', needs: (s) => !!s.orders },
  visitors: { name: 'Ziyaretçiler ve Maliyetler', detail: 'Strateji · yeni / dönen ziyaretçi, yeni müşteri edinme, sepet ve satın alma maliyeti', needs: (s) => s.ga4 },
  channels_campaigns: { name: 'Kanal ve Kampanya Kırılımı', detail: 'Strateji · Meta, Google, organik, doğrudan; RM / yeni kullanıcı kampanya grupları', needs: (s) => s.ga4 },
  product_funnel: { name: 'Ürün Hunisi (En İyi / En Kötü 20)', detail: 'Strateji · inceleyen kişi, oturum, süre, sepet, ödeme, satın alma; fırsat ürünleri', needs: (s) => s.ga4 },
  ad_products: { name: 'Reklam Harcaması ve Ürünler', detail: 'Strateji · Google Alışveriş ürün harcaması, tıklama alıp satmayan, harcama alamayan ürünler', needs: (s) => s.google_ads },
  size_gaps: { name: 'Beden Eksikliği', detail: 'Strateji · talep görüp bedeni tükenmiş ürünler (web sitesi stoğu)', needs: (s) => s.ga4 },
  checkout: { name: 'Ödeme Adımı', detail: 'Strateji · ödeme hunisi, terk oranı, cihaz ve hafta kırılımı', needs: (s) => s.ga4 },
  brand_search: { name: 'Marka Araması', detail: 'Strateji · Search Console marka sorguları, aylık kıyas', needs: () => true },
  creatives: { name: 'Reklam Kreatifleri', detail: 'Strateji · Meta reklam görselleri, harcama ve getirisi', needs: (s) => s.meta_ads },
  stock: { name: 'Stok Sağlığı', detail: 'Pixa · tüm platformlardaki stok, satış adedi ve tutarı, kaç günlük stok, AI özeti', needs: (s) => !!s.pixa },
};
const PRESETS: [string, string][] = [['this_month', 'Bu Ay'], ['last_7', 'Son 7 Gün'], ['last_30', 'Son 30 Gün'], ['last_month', 'Geçen Ay']];
const ACTIONS: Record<string, string> = {
  view: 'görüntüledi', login: 'giriş yaptı', login_failed: 'hatalı kod girdi', code_sent: 'giriş kodu gönderildi',
  code_denied: 'yetkisiz adres kod istedi', code_mail_failed: 'kod e-postası gönderilemedi', viewer_added: 'erişim verildi',
  viewer_removed: 'erişim kaldırıldı', preview: 'önizleme açtı', pdf_download: 'PDF indirdi',
  pdf_sent_weekly: 'haftalık PDF gönderildi', pdf_sent_monthly: 'aylık PDF gönderildi',
  pdf_failed_weekly: 'haftalık PDF gönderilemedi', pdf_failed_monthly: 'aylık PDF gönderilemedi',
  invite_sent: 'davet e-postası gönderildi', link_login: 'e-postadaki butonla giriş yaptı', link_login_failed: 'geçersiz bağlantı denendi',
  invite_failed: 'davet e-postası gönderilemedi',
};
const MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

function dLabel(iso: string) { const [y, m, d] = iso.split('-').map(Number); return `${d} ${MONTHS[m - 1]} ${y}`; }
function when(ts: string | null) {
  if (!ts) return '';
  const d = new Date(ts.replace(' ', 'T') + 'Z');
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
function ago(ts: string | null) {
  if (!ts) return 'Henüz giriş yapmadı';
  const days = Math.floor((Date.now() - new Date(ts.replace(' ', 'T') + 'Z').getTime()) / 86400000);
  return days <= 0 ? 'Son giriş bugün' : `Son giriş ${days} gün önce`;
}

async function api(path: string, opts: RequestInit = {}) {
  const res = await fetch(API + path, { ...opts, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}`, ...(opts.headers || {}) } });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    const err: any = new Error(typeof j.detail === 'string' ? j.detail : `İstek başarısız (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res;
}

const inputStyle: React.CSSProperties = {
  height: 40, padding: '0 12px', borderRadius: 10, border: `1px solid ${T.borderMid}`, background: T.surface,
  color: T.text, fontFamily: 'inherit', fontSize: 13, width: '100%', boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: T.textMid };

function Toggle({ on, onChange, label, disabled }: { on: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={() => onChange(!on)}
      style={{ width: 44, height: 26, borderRadius: 999, border: 'none', padding: 3, cursor: disabled ? 'default' : 'pointer', flexShrink: 0,
        background: on ? T.accent : T.trackBg, display: 'flex', justifyContent: on ? 'flex-end' : 'flex-start', opacity: disabled ? 0.5 : 1, transition: 'background .15s' }}>
      <span style={{ width: 20, height: 20, borderRadius: 999, background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
    </button>
  );
}

interface ClientOpt { id: number; name: string; reports: number }

const URL_PARAMS = new URLSearchParams(window.location.search);

export default function ClientReportAdmin({ activeClientId }: { activeClientId?: number }) {
  const [tab, setTab] = useState<'report' | 'integrations'>(URL_PARAMS.get('tab') === 'entegrasyonlar' ? 'integrations' : 'report');
  const [clients, setClients] = useState<ClientOpt[] | null>(null);
  const [clientId, setClientId] = useState<number | null>(null);
  const [switchFocus, setSwitchFocus] = useState(false);
  const [reports, setReports] = useState<Report[] | null>(null);
  const [rep, setRep] = useState<Report | null>(null);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [err, setErr] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [sendInvite, setSendInvite] = useState(true);
  const [draft, setDraft] = useState<{ title: string; default_preset: string; service_day: string; compare_default: boolean; compare_mode: 'prev' | 'yoy'; fixed: boolean; fixed_start: string; fixed_end: string } | null>(null);

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };
  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key); setErr('');
    try { await fn(); } catch (e: any) { setErr(e.message); }
    setBusy('');
  };

  const openReport = useCallback(async (id: number) => {
    const r: Report = await (await api(`/${id}`)).json();
    setRep(r);
    setDraft({ title: r.title, default_preset: r.default_preset, service_day: r.service_day ? String(r.service_day) : '', compare_default: r.compare_default, compare_mode: r.compare_mode || 'prev', fixed: !!(r.fixed_start && r.fixed_end), fixed_start: r.fixed_start || '', fixed_end: r.fixed_end || '' });
    const l = await (await api(`/${id}/access-log?limit=25`)).json();
    setLog(l.entries);
  }, []);

  // Müşteri seçici: panelde seçili müşteriyle açılır, sayfa içinde bağımsız değiştirilir.
  useEffect(() => {
    api('/admin/clients').then((r) => r.json()).then((j) => {
      const list: ClientOpt[] = j.clients;
      setClients(list);
      const fromUrl = Number(URL_PARAMS.get('client')) || 0;
      const pick = list.find((c) => c.id === fromUrl) || list.find((c) => c.id === activeClientId) || list.find((c) => c.reports > 0) || list[0];
      setClientId(pick ? pick.id : null);
    }).catch((e) => { if (e.status === 403) setForbidden(true); else setErr(e.message); });
  }, [activeClientId]);

  useEffect(() => {
    if (!clientId) return;
    setReports(null); setRep(null); setDraft(null); setLog([]); setErr('');
    api(`?client_id=${clientId}`).then((r) => r.json()).then(async (j) => {
      setReports(j.reports);
      if (j.reports.length) await openReport(j.reports[0].id);
    }).catch((e) => setErr(e.message));
  }, [clientId, openReport]);

  const update = (body: object, note = 'Kaydedildi') => run('save', async () => {
    const r: Report = await (await api(`/${rep!.id}`, { method: 'PUT', body: JSON.stringify(body) })).json();
    setRep(r); flash(note);
  });

  if (forbidden) return <Card><p style={{ margin: 0, color: T.textMid }}>Raporlar yalnızca yöneticilere açıktır.</p></Card>;
  if (clients === null) return <Card>{err ? <p style={{ margin: 0, color: T.redText }}>{err}</p> : <PoyaisSpinner size={32} pad={12}/>}</Card>;
  if (!clients.length) return <Card><p style={{ margin: 0, color: T.textMid }}>Aktif müşteri yok.</p></Card>;

  const header = (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <div>
        {/* Müşteri Yönetimi / Ayarlar başlığındaki breadcrumb ile aynı: Admin › Raporlar (bulunulan sayfa vurgu renginde) */}
        <nav aria-label="Konum" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 500, color: T.textDimAlt }}>
          <span>Admin</span><span aria-hidden="true" style={{ fontSize: 14 }}>›</span>
          <span aria-current="page" style={{ color: T.accent, fontWeight: 700 }}>Raporlar</span>
        </nav>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 22, fontWeight: 800, color: T.text, letterSpacing: '-0.02em', marginTop: 4 }}>
          {/* Görünen etiket seçili müşterinin adı kadar; üstünde şeffaf yerel <select> (klavye ve ekran okuyucu için) */}
          <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 10px 2px 12px', borderRadius: 12,
            border: `1px solid ${switchFocus ? T.text : T.borderMid}`, background: T.surface }}>
            {clients.find((c) => c.id === clientId)?.name || 'Müşteri seçin'}
            <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 20, color: T.textDim }}>expand_more</span>
            <select aria-label="Müşteri seçin" value={clientId ?? ''} onChange={(e) => setClientId(Number(e.target.value))}
              onFocus={() => setSwitchFocus(true)} onBlur={() => setSwitchFocus(false)}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', font: 'inherit', fontSize: 14 }}>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.reports ? '' : ' (rapor yok)'}</option>)}
            </select>
          </span>
          {rep && <span>{rep.title}</span>}
          {rep && !rep.is_active && <Badge color={T.red}>Yayında değil</Badge>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {msg && <span style={{ fontSize: 12, color: T.greenText, fontWeight: 700 }}>{msg}</span>}
        {reports && reports.length > 1 && rep && (
          <select aria-label="Rapor seçin" value={rep.id} onChange={(e) => run('open', () => openReport(Number(e.target.value)))} style={{ ...inputStyle, width: 'auto' }}>
            {reports.map((r) => <option key={r.id} value={r.id}>{r.title} · /r/{r.slug}</option>)}
          </select>
        )}
        {rep && (
          <Button variant="outline" disabled={busy === 'preview'} onClick={() => run('preview', async () => {
            const w = window.open('', '_blank');
            const j = await (await api(`/${rep.id}/preview-token`, { method: 'POST' })).json();
            const url = `${j.url}?preview=${encodeURIComponent(j.token)}`;
            if (w) w.location.href = url; else window.location.href = url;
          })}>İzleyici Gibi Önizleyin</Button>
        )}
      </div>
    </div>
  );
  const tabs = (
    <div role="tablist" aria-label="Rapor sayfası" style={{ display: 'flex', gap: 4, background: T.trackBg, borderRadius: 11, padding: 4, width: 'fit-content', overflowX: 'auto', maxWidth: '100%' }}>
      {([['report', 'Rapor Ayarları'], ['integrations', 'Entegrasyonlar']] as const).map(([k, l]) => (
        <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
          style={{ padding: '8px 20px', borderRadius: 8, fontSize: 12.5, fontWeight: tab === k ? 700 : 500, cursor: 'pointer', whiteSpace: 'nowrap',
            background: tab === k ? T.surface : 'transparent', color: tab === k ? T.text : T.textMidAlt, border: 'none',
            boxShadow: tab === k ? '0 1px 2px rgba(20,22,43,.1)' : 'none', transition: 'all .15s' }}>{l}</button>
      ))}
    </div>
  );
  const shell = (body: React.ReactNode) => <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>{header}{tabs}{body}</div>;
  const refreshSources = () => { if (rep) openReport(rep.id).catch(() => {}); };
  if (tab === 'integrations' && clientId) return shell(<ReportIntegrations key={clientId} clientId={clientId} onChanged={refreshSources} />);

  if (reports === null) return shell(<Card>{err ? <p style={{ margin: 0, color: T.redText }}>{err}</p> : <PoyaisSpinner size={32} pad={12}/>}</Card>);

  if (!reports.length) {
    return shell(
      <Card pad={32} style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'flex-start', maxWidth: 640 }}>
        <SectionHeader title="Bu müşteri için henüz paylaşılabilir rapor yok" />
        <p style={{ margin: 0, color: T.textMid, fontSize: 14, lineHeight: '22px' }}>
          Rapor, müşterinin tarih seçip filtre uygulayabildiği canlı bir sayfadır. Önce Entegrasyonlar sekmesinden müşterinin reklam,
          analitik ve sipariş kaynaklarını bağlayın; rapor oluşturulunca geçmiş veriler arka planda yüklenir. Bağlı olmayan kaynakların
          bölümleri otomatik gizlenir. Yetkili e-postalara kişisel bağlantı gider, panel hesabı ya da kod gerekmez.
        </p>
        {err && <p style={{ margin: 0, color: T.redText, fontSize: 13 }}>{err}</p>}
        <Button variant="solid" disabled={busy === 'create'} onClick={() => run('create', async () => {
          const r: Report = await (await api('', { method: 'POST', body: JSON.stringify({ client_id: clientId, title: 'Performans Raporu' }) })).json();
          setReports([r]);
          setClients((cs) => (cs || []).map((c) => (c.id === clientId ? { ...c, reports: c.reports + 1 } : c)));
          await openReport(r.id);
        })} style={{ color: '#fff' }}>{busy === 'create' ? 'Oluşturuluyor…' : 'Rapor Oluşturun'}</Button>
      </Card>
    );
  }
  if (!rep || !draft) return shell(<Card>{err ? <p style={{ margin: 0, color: T.redText }}>{err}</p> : <PoyaisSpinner size={32} pad={12}/>}</Card>);

  const link = `${window.location.origin}/r/${rep.slug}`;
  const order = rep.sections.order;
  const disabled = new Set(rep.sections.disabled);
  const draftDirty = draft.title !== rep.title || draft.default_preset !== rep.default_preset
    || draft.service_day !== (rep.service_day ? String(rep.service_day) : '') || draft.compare_default !== rep.compare_default
    || draft.compare_mode !== (rep.compare_mode || 'prev')
    || draft.fixed !== !!(rep.fixed_start && rep.fixed_end) || (draft.fixed && (draft.fixed_start !== (rep.fixed_start || '') || draft.fixed_end !== (rep.fixed_end || '')));
  const move = (k: string, dir: -1 | 1) => {
    const i = order.indexOf(k), j = i + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order]; [next[i], next[j]] = [next[j], next[i]];
    update({ section_order: next });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {header}
      {tabs}
      {err && <Card pad={14} style={{ borderColor: T.red }}><span style={{ color: T.redText, fontSize: 13 }}>{err}</span></Card>}

      <div style={{ display: 'grid', gap: 18, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 440px), 1fr))', alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Card pad={0} style={{ overflow: 'hidden' }}>
            <div style={{ padding: '18px 22px', borderBottom: `1px solid ${T.border}` }}>
              <SectionHeader title="Bölümler" />
              <div style={{ fontSize: 12, color: T.textDim, marginTop: -8 }}>Sırayı oklarla değiştirin. Bağlı olmayan kaynakların bölümleri raporda otomatik gizlenir.</div>
            </div>
            {order.map((k, i) => {
              const def = SECTIONS[k];
              if (!def) return null;
              const connected = def.needs(rep.sources);
              const on = connected && !disabled.has(k);
              return (
                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 22px', borderBottom: i < order.length - 1 ? `1px solid ${T.border}` : undefined }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <button type="button" aria-label={`${def.name} yukarı taşı`} disabled={i === 0 || busy === 'save'} onClick={() => move(k, -1)}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.textDim, padding: 2, lineHeight: 0, opacity: i === 0 ? 0.3 : 1 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>keyboard_arrow_up</span></button>
                    <button type="button" aria-label={`${def.name} aşağı taşı`} disabled={i === order.length - 1 || busy === 'save'} onClick={() => move(k, 1)}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.textDim, padding: 2, lineHeight: 0, opacity: i === order.length - 1 ? 0.3 : 1 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>keyboard_arrow_down</span></button>
                  </div>
                  <div style={{ flexGrow: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: connected ? T.text : T.textDim }}>{def.name}</div>
                    <div style={{ fontSize: 12, color: T.textDim }}>{def.detail}</div>
                  </div>
                  {connected ? (
                    <Toggle on={on} label={`${def.name} bölümünü göster`} disabled={busy === 'save'}
                      onChange={(v) => update({ disabled_sections: v ? rep.sections.disabled.filter((x) => x !== k) : [...rep.sections.disabled, k] })} />
                  ) : <span style={{ fontSize: 12, color: T.textDim }}>Kaynak bağlı değil</span>}
                </div>
              );
            })}
          </Card>

          <Card>
            <SectionHeader title="Varsayılan Görünüm" />
            <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
              <label style={labelStyle}>Rapor başlığı
                <input style={inputStyle} value={draft.title} maxLength={120} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
              </label>
              <label style={labelStyle}>Açılışta tarih aralığı
                <select style={inputStyle} value={draft.fixed ? 'fixed' : draft.default_preset} onChange={(e) => e.target.value === 'fixed'
                  ? setDraft({ ...draft, fixed: true })
                  : setDraft({ ...draft, fixed: false, default_preset: e.target.value })}>
                  {PRESETS.map(([k, l]) => <option key={k} value={k}>{l} (kayan)</option>)}
                  <option value="fixed">Sabit tarih aralığı</option>
                </select>
              </label>
              {draft.fixed && (
                <>
                  <label style={labelStyle}>Sabit başlangıç
                    <input type="date" style={inputStyle} value={draft.fixed_start} onChange={(e) => setDraft({ ...draft, fixed_start: e.target.value })} />
                  </label>
                  <label style={labelStyle}>Sabit bitiş
                    <input type="date" style={inputStyle} value={draft.fixed_end} onChange={(e) => setDraft({ ...draft, fixed_end: e.target.value })} />
                  </label>
                </>
              )}
              <label style={labelStyle}>Hizmet dönemi başlangıç günü
                <select style={inputStyle} value={draft.service_day} onChange={(e) => setDraft({ ...draft, service_day: e.target.value })}>
                  <option value="">Yok (takvim ayı)</option>
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>Her ayın {d}'i</option>)}
                </select>
              </label>
              <label style={labelStyle}>Karşılaştırma dönemi
                <select style={inputStyle} value={draft.compare_mode} onChange={(e) => setDraft({ ...draft, compare_mode: e.target.value as 'prev' | 'yoy' })}>
                  <option value="prev">Önceki dönem</option>
                  <option value="yoy">Geçen yılın aynı dönemi (LFL)</option>
                </select>
              </label>
              <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: 10, alignSelf: 'end', height: 40 }}>
                <Toggle on={draft.compare_default} label="Önceki dönemle karşılaştırma açık başlasın" onChange={(v) => setDraft({ ...draft, compare_default: v })} />
                Önceki dönemle karşılaştır
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <Button variant="solid" style={{ color: '#fff' }} disabled={!draftDirty || busy === 'save'} onClick={() => update({
                title: draft.title, default_preset: draft.default_preset, compare_default: draft.compare_default,
                compare_mode: draft.compare_mode,
                fixed_start: draft.fixed ? draft.fixed_start : null, fixed_end: draft.fixed ? draft.fixed_end : null, service_day: draft.service_day ? Number(draft.service_day) : null,
              })}>{busy === 'save' ? 'Kaydediliyor…' : 'Kaydedin'}</Button>
            </div>
          </Card>

          <Card>
            <SectionHeader title="Dönem Yorumu" />
            <p style={{ margin: '0 0 14px', fontSize: 13, color: T.textMid, lineHeight: '20px' }}>
              Poyais One AI, raporun açılıştaki dönemi için 3 maddelik bir yorum yazar ve saklar; otomatik e-postalarda gönderilen döneme ait yorum da gönderimden önce üretilir.
              Veri değiştiyse yorumu yeniden üretebilirsiniz.
            </p>
            <Button variant="outline" disabled={busy === 'comment'} onClick={() => run('comment', async () => {
              await api(`/${rep.id}/commentary`, { method: 'POST', body: JSON.stringify({}) }); flash('Yorum yeniden üretildi');
            })}>{busy === 'comment' ? 'Üretiliyor… (~15 sn)' : 'Yorumu Yeniden Üretin'}</Button>
          </Card>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Card>
            <SectionHeader title="Paylaşım ve Erişim" right={
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, color: T.textMid }}>
                Yayında <Toggle on={rep.is_active} label="Rapor yayında" disabled={busy === 'save'}
                  onChange={(v) => { if (v || window.confirm('Rapor yayından kaldırılsın mı? Link çalışmayı durdurur ve izleyiciler erişemez.')) update({ is_active: v }, v ? 'Rapor yayında' : 'Rapor yayından kaldırıldı'); }} />
              </label>} />
            <div style={{ ...labelStyle, marginBottom: 16 }}>Rapor Linki
              <div style={{ display: 'flex', gap: 8 }}>
                <input readOnly value={link} style={{ ...inputStyle, background: T.subtleBg }} onFocus={(e) => e.target.select()} aria-label="Rapor linki" />
                <Button variant="ghost" onClick={() => { navigator.clipboard?.writeText(link); flash('Link kopyalandı'); }}>Kopyalayın</Button>
              </div>
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.textMid, marginBottom: 8 }}>Yetkili E-Postalar</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rep.viewers.map((v) => (
                <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px', borderRadius: 10, border: `1px solid ${T.border}` }}>
                  <span style={{ width: 30, height: 30, borderRadius: 999, background: T.amberTint, color: T.amberDeepText, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
                    {v.email.charAt(0).toLocaleUpperCase('tr')}</span>
                  <div style={{ flexGrow: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.email}</div>
                    <div style={{ fontSize: 11, color: T.textDim }}>{ago(v.last_login_at)}</div>
                  </div>
                  <button type="button" title="Kişisel linki kopyalayın (kod girmeden açılır)" aria-label={`${v.email} kişisel linkini kopyala`} disabled={!v.personal_link}
                    onClick={() => { if (v.personal_link) { navigator.clipboard?.writeText(v.personal_link); flash(`${v.email} için kişisel link kopyalandı`); } }}
                    style={{ width: 34, height: 34, border: 'none', background: 'none', cursor: 'pointer', color: T.textDim, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>link</span></button>
                  <button type="button" title="Davet e-postasını tekrar gönderin" aria-label={`${v.email} adresine daveti tekrar gönder`} disabled={!!busy}
                    onClick={() => run('invite', async () => { await api(`/${rep.id}/viewers/${v.id}/invite`, { method: 'POST' }); flash(`Davet ${v.email} adresine gönderildi`); })} style={{ width: 34, height: 34, border: 'none', background: 'none', cursor: 'pointer', color: T.textDim, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{busy === 'invite' ? 'hourglass_empty' : 'mail'}</span></button>
                  <button type="button" title="Erişimi kaldırın" aria-label={`${v.email} erişimini kaldır`} disabled={busy === 'viewer'}
                    onClick={() => { if (window.confirm(`${v.email} erişimi kaldırılsın mı? Açık oturumu da hemen kapanır.`)) run('viewer', async () => { setRep(await (await api(`/${rep.id}/viewers/${v.id}`, { method: 'DELETE' })).json()); flash('Erişim kaldırıldı'); }); }}
                    style={{ width: 36, height: 36, border: 'none', background: 'none', cursor: 'pointer', color: T.textDim, borderRadius: 8 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span></button>
                </div>
              ))}
              {rep.viewers.length === 0 && <div style={{ fontSize: 12, color: T.textDim }}>Henüz yetkili e-posta yok.</div>}
              <form style={{ display: 'flex', gap: 8 }} onSubmit={(e) => {
                e.preventDefault();
                run('viewer', async () => {
                  const r = await (await api(`/${rep.id}/viewers`, { method: 'POST', body: JSON.stringify({ email: newEmail, send_invite: sendInvite }) })).json();
                  setRep(r); setNewEmail('');
                  flash(r.invite_sent === true ? 'Erişim verildi, davet e-postası gönderildi' : r.invite_sent === false ? 'Erişim verildi, ancak davet e-postası gönderilemedi' : 'Erişim verildi');
                });
              }}>
                <input type="email" required placeholder="ad@marka.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} style={inputStyle} aria-label="Yeni yetkili e-posta" />
                <Button variant="solid" type="submit" disabled={busy === 'viewer' || !newEmail} style={{ color: '#fff' }}>Ekleyin</Button>
              </form>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.textMid, cursor: 'pointer' }}>
                <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--py-accent)' }} />
                Eklenen kişiye davet e-postası gönderilsin.
              </label>
              <div style={{ fontSize: 11, color: T.textDim, lineHeight: '16px' }}>
                Eklediğiniz adreslere otomatik rapor e-postaları hemen gitmeye başlar; kişinin giriş yapması ya da kod girmesi gerekmez.
                E-postalardaki "Raporu Açın" butonu kişiye özeldir: tıklayan kod girmeden rapora girer ve 30 gün oturumu açık kalır. Link simgesiyle aynı kişisel linki kopyalayıp kendiniz de iletebilirsiniz.
                Genel rapor linkini açıp adresini yazanlar kodla girer. Kişiyi listeden çıkardığınızda kişisel linki de çalışmaz.
              </div>
            </div>
          </Card>

          <Card>
            <SectionHeader title="Otomatik Gönderim" />
            {[
              { k: 'weekly_enabled', name: 'Haftalık PDF', on: rep.weekly_enabled, detail: `Her Pazartesi 09:00 · Sonraki Gönderim: ${dLabel(rep.next_weekly_period[0])} – ${dLabel(rep.next_weekly_period[1])} Dönemi`, kind: 'weekly' },
              { k: 'monthly_enabled', name: 'Aylık Özet', on: rep.monthly_enabled,
                detail: `${rep.service_day ? `Her Ayın ${rep.service_day}'i` : 'Her Ayın 1\'i'} 09:00 · Sıradaki Gönderim: ${dLabel(rep.next_monthly_period[0])} – ${dLabel(rep.next_monthly_period[1])} Dönemi`, kind: 'monthly' },
            ].map((s) => (
              <div key={s.k} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `1px solid ${T.border}` }}>
                <div style={{ flexGrow: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: T.textDim }}>{s.detail}</div>
                  <button type="button" disabled={!!busy} onClick={() => run(`test-${s.kind}`, async () => {
                    const j = await (await api(`/${rep.id}/send-test`, { method: 'POST', body: JSON.stringify({ kind: s.kind }) })).json();
                    flash(`Deneme e-postası ${j.recipients[0]} adresine gönderildi`);
                  })} style={{ marginTop: 4, border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: T.accentDeep, fontFamily: 'inherit' }}>
                    {busy === `test-${s.kind}` ? 'Gönderiliyor…' : 'Kendime deneme gönder.'}</button>
                </div>
                <Toggle on={s.on} label={s.name} disabled={busy === 'save'} onChange={(v) => update({ [s.k]: v }, v ? `${s.name} açıldı` : `${s.name} kapatıldı`)} />
              </div>
            ))}
            <div style={{ fontSize: 11, color: T.textDim, lineHeight: '16px', marginTop: 10 }}>
              {rep.viewers.length > 0
                ? <><strong style={{ color: T.textMid }}>Alıcılar:</strong> {rep.viewers.map((v) => v.email).join(', ')}. </>
                : <strong style={{ color: T.redText }}>Henüz alıcı yok: Paylaşım ve Erişim'den e-posta ekleyin, otomatik raporlar eklediğiniz adreslere gider. </strong>}
              PDF, gönderim anındaki rapor görüntüsüdür ve her alıcıya ayrı gider. Linkteki canlı rapor her zaman günceldir.
            </div>
          </Card>

          <Card>
            <SectionHeader title="Erişim Kaydı" right={<span style={{ fontSize: 11, color: T.textDim }}>1 yıl saklanır</span>} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: T.textMid, maxHeight: 280, overflowY: 'auto' }}>
              {log.map((e, i) => (
                <div key={i} style={{ display: 'flex', gap: 8 }}>
                  <span style={{ color: T.textDim, whiteSpace: 'nowrap' }}>{when(e.created_at)}</span>
                  <span style={{ minWidth: 0 }}>{e.email && <strong style={{ color: T.text, fontWeight: 700 }}>{e.email} · </strong>}{ACTIONS[e.action] || e.action}.</span>
                </div>
              ))}
              {log.length === 0 && <span style={{ color: T.textDim }}>Henüz kayıt yok.</span>}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
